import { api } from '@/api';
import { getCustomerProfile } from '@/features/auth/authService';
import { fetchProfileBookings } from '@/features/profile/profileService';
import { createAsyncThunk } from '@/store/utils';
import type {
  CustomerProfileDetailsResponseDTO,
  GetVouchersResponseDTO,
} from '@/types/dto/agent';
import type {
  UpdateCustomerProfileRequestDTO,
  UpdateCustomerProfileResponseDTO,
} from '@/types/dto/customer-profile';
import type {
  BookingSearchResponse,
  UpdateCustomerProfileRequest,
} from '@/types/profile';
import { getTokensRef } from '@/utils/auth';
import { createAction } from '@reduxjs/toolkit';

export const clearProfile = createAction('profile/clearProfile');
export const clearBookings = createAction('profile/clearBookings');

export const getProfile =
  createAsyncThunk<CustomerProfileDetailsResponseDTO | null>(
    'profile/getProfile',
    async () => {
      const tokensRef = getTokensRef();

      if (!tokensRef) {
        return null;
      }

      try {
        return await getCustomerProfile();
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        return null;
      }
    }
  );

export const updateProfile = createAsyncThunk<
  UpdateCustomerProfileResponseDTO,
  UpdateCustomerProfileRequest
>('profile/updateProfile', async (profileData, { getState }) => {
  const { current } = getState().profile;

  if (!current?.id) {
    throw new Error('No customer profile ID available');
  }

  return (
    await api.customerProfileApi.CustomerProfiles_UpdateCustomerProfile(
      { customerProfileId: current.id },
      profileData as UpdateCustomerProfileRequestDTO
    )
  ).data;
});

export const getProfileActiveTickets = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileActiveTickets', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileCode: `${current?.accountCode}`,
      customerProfileBookingsActivityType: 'ACTIVE',
      productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
    },
    offset
  );
});

export const getProfileHistoryTickets = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileHistoryTickets', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileCode: `${current?.accountCode}`,
      customerProfileBookingsActivityType: 'INACTIVE',
      productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
    },
    offset
  );
});

export const getProfileActiveTravelPasses = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileActiveTravelPasses', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileCode: `${current?.accountCode}`,
      customerProfileBookingsActivityType: 'ACTIVE',
      productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
    },
    offset
  );
});

export const getProfileHistoryTravelPasses = createAsyncThunk<
  BookingSearchResponse,
  number | undefined
>('profile/getProfileHistoryTravelPasses', async (offset = 0, { getState }) => {
  const { current } = getState().profile;

  return fetchProfileBookings(
    {
      customerProfileCode: `${current?.accountCode}`,
      customerProfileBookingsActivityType: 'INACTIVE',
      productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
    },
    offset
  );
});

export const getProfileActiveVouchers =
  createAsyncThunk<GetVouchersResponseDTO>(
    'profile/getProfileActiveVouchers',
    async () => {
      return (
        await api.agentApi.CustomerProfile_GetCustomerProfileVouchers({
          ActivityType: 'ACTIVE',
        })
      ).data;
    }
  );

export const getProfileHistoryVouchers =
  createAsyncThunk<GetVouchersResponseDTO>(
    'profile/getProfileHistoryVouchers',
    async () => {
      return (
        await api.agentApi.CustomerProfile_GetCustomerProfileVouchers({
          ActivityType: 'INACTIVE',
        })
      ).data;
    }
  );
